import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { useDebounce } from "@src/appV2/lib/utils/useDebounce";
import { Slider, Text } from "@src/appV2/redesign/system/ui-components";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getFilterSliderStyles } from "./sliderStyles";

const PAY_MAX_CENTS = 30_000;
const PAY_STEP_CENTS = 1000;

function floorToStep(value: number): number {
  return Math.floor(value / PAY_STEP_CENTS) * PAY_STEP_CENTS;
}

function centsToDisplayValue(cents: number): string {
  return `$${Math.round(cents / 100)}`;
}

function getMarks(min: number, max: number): Array<{ value: number; label: string }> {
  const marks: Array<{ value: number; label: string }> = [];

  if (min % 10_000 !== 0) {
    marks.push({ value: min, label: `$${min / 100}` });
  }

  for (let cents = min; cents <= max; cents += PAY_STEP_CENTS) {
    if (cents % 10_000 === 0) {
      marks.push({ value: cents, label: `$${cents / 100}` });
    }
  }

  return marks;
}

interface PayFilterProps {
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  minCents: number;
}

export function PayFilter(props: PayFilterProps) {
  const { value, onChange, minCents } = props;

  const payMinCents = floorToStep(minCents);
  const sliderValue = value ?? payMinCents;
  const [localValue, setLocalValue] = useState(sliderValue);
  const pendingRef = useRef<number | undefined>(undefined);

  const handleDebouncedSave = useCallback(
    (newValue: number) => {
      onChange(newValue <= payMinCents ? undefined : newValue);
    },
    [onChange, payMinCents]
  );

  useDebounce({
    value: localValue,
    debounceTimeInMs: 300,
    onUpdateValue: handleDebouncedSave,
  });

  useEffect(() => {
    const externalValue = value ?? payMinCents;
    if (!isDefined(pendingRef.current) || pendingRef.current === externalValue) {
      pendingRef.current = undefined;
      setLocalValue(externalValue);
    }
  }, [value, payMinCents]);

  const marks = useMemo(() => getMarks(payMinCents, PAY_MAX_CENTS), [payMinCents]);

  const displayLabel =
    localValue <= payMinCents ? "Any" : `${centsToDisplayValue(localValue)} per visit`;

  return (
    <Stack spacing={5}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack spacing={2}>
          <Text semibold variant="body1">
            Minimum Pay
          </Text>
          <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.tertiary })}>
            Per visit
          </Text>
        </Stack>
        <Text
          variant="body2"
          sx={(theme) => ({
            border: `1px solid ${theme.palette.border?.subtle}`,
            borderRadius: theme.borderRadius?.xSmall,
            padding: 3,
          })}
        >
          {displayLabel}
        </Text>
      </Stack>

      <Slider
        aria-label="Minimum Pay Slider"
        value={localValue}
        min={payMinCents}
        max={PAY_MAX_CENTS}
        step={PAY_STEP_CENTS}
        marks={marks}
        valueLabelDisplay="auto"
        valueLabelFormat={centsToDisplayValue}
        sx={(theme) => getFilterSliderStyles(theme)}
        onChange={(_event, sliderNewValue) => {
          const newValue = sliderNewValue as number;
          pendingRef.current = newValue;
          setLocalValue(newValue);
        }}
      />
    </Stack>
  );
}
